/**
 * Cart icon - keeps the header bubble in sync with cart.item_count.
 *
 * Listens for CartUpdateEvent and CartAddEvent, then refetches /cart.js
 * to read the authoritative item count and update the bubble in place.
 *
 * @typedef {Object} CartIconRefs
 * @property {HTMLElement} bubble
 * @property {HTMLElement} count
 *
 * @extends {Component<CartIconRefs>}
 */

import { Component } from "@theme/component";
import { CartAddEvent, CartUpdateEvent } from "@theme/events";

class CartIconComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#refresh);
    document.addEventListener(CartUpdateEvent.eventName, this.#refresh);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#refresh);
    document.removeEventListener(CartUpdateEvent.eventName, this.#refresh);
  }

  #refresh = async () => {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;

      const cart = await response.json();
      this.#setCount(cart.item_count);
    } catch (error) {
      console.error("[cart-icon] refresh failed:", error);
    }
  };

  /**
   * @param {number} count
   */
  #setCount(count) {
    const { bubble, count: countEl } = this.refs;
    if (!bubble || !countEl) return;

    countEl.textContent = String(count);
    bubble.setAttribute("hidden", count === 0 ? "true" : "false");
  }
}

if (!customElements.get("cart-icon-component")) {
  customElements.define("cart-icon-component", CartIconComponent);
}
